import React, { useMemo, useState } from "react";
import { MagnifyingGlass, BookOpen, ArrowRight, Envelope, Question } from "@phosphor-icons/react";

const SECTIONS = [
  {
    id: "getting-started", title: "Getting Started", cat: "Start here",
    body: [
      { h: "1. Create your account", p: "Go to /register, enter name, email and password. You'll be logged in immediately." },
      { h: "2. Set up your company", p: "First-time users are redirected to Company Setup. Enter Legal Name, GSTIN, State, PAN, address. Toggle e-Invoice auto-generation if you handle B2B invoices ≥ ₹50,000." },
      { h: "3. Add masters", p: "Add at least one Customer (Sales → Customers), one Vendor (Purchases → Vendors), and a Product/Service (Catalog → Products & Services). Products can be goods (HSN) or services (SAC)." },
      { h: "4. Create your first invoice", p: "Sales → Invoices → New Invoice. Pick a customer, add lines, save. If B2B ≥ ₹50k and e-Invoice is enabled, IRN + QR code are generated automatically." },
      { h: "5. Record payment", p: "Open the invoice, click Record Payment, enter amount/method/reference. Balance updates and a payment voucher posts automatically." },
    ],
  },
  {
    id: "company-setup", title: "Company Setup", cat: "Setup",
    body: [
      { h: "GSTIN & State", p: "The first two digits of your GSTIN drive intra vs inter-state GST calculation. Same state = CGST + SGST; different state = IGST." },
      { h: "e-Invoice toggle", p: "When ON, B2B invoices ≥ ₹50,000 auto-generate IRN + QR code. Configure GSP under Compliance → e-Invoice GSP for real IRN calls (otherwise a mock IRN is used for demo)." },
      { h: "Global mode", p: "Settings → Tax Mode → 'Global VAT / Sales Tax' switches off India GST fields. Use USD/EUR/GBP/etc as base currency." },
    ],
  },
  {
    id: "invoices", title: "Invoices", cat: "Sales",
    body: [
      { h: "Create an invoice", p: "Sales → Invoices → New Invoice. Select customer, add line items with quantity, price, GST rate. HSN/SAC auto-populate from product." },
      { h: "GST calculation", p: "Intra-state (same GSTIN state code) posts CGST + SGST split. Inter-state posts full IGST. B2C (no customer GSTIN) is treated as intra-state." },
      { h: "IRN + QR", p: "For B2B invoices ≥ ₹50k, IRN generates on save. View the invoice detail page to see IRN, Ack No, Ack Date and scannable QR." },
      { h: "PDF & Email", p: "Invoice detail → PDF (downloads GST-format A4 PDF) or Email (sends to customer with Resend if configured, else logs)." },
      { h: "Payment with TDS", p: "Record Payment → toggle 'Deduct TDS' → pick section (194J 10%, 194C 2%, 194H 5%, 194I 10%, 194Q 0.1%). Net-received posts to Bank, TDS to TDS Receivable, both credit Debtors." },
    ],
  },
  {
    id: "recurring", title: "Recurring Invoices", cat: "Sales",
    body: [
      { h: "Create a schedule", p: "Sales → Recurring → New Schedule. Pick customer, frequency (weekly/monthly/quarterly/yearly), start date, optional end date, and line items." },
      { h: "How it runs", p: "A background worker checks every hour. When next_run ≤ today, an invoice is auto-generated and a balanced voucher posted. You can also click 'Run now' to trigger manually." },
    ],
  },
  {
    id: "credit-notes", title: "Credit Notes", cat: "Sales",
    body: [
      { h: "Issue a credit note", p: "Sales → Credit Notes → New Credit Note. Select the invoice, enter reason and amount." },
      { h: "Accounting", p: "Automatically posts Dr Sales Returns, Cr Sundry Debtors. GSTR-1 aggregations will include credit notes in future filings." },
    ],
  },
  {
    id: "purchases", title: "Vendor Bills", cat: "Purchases",
    body: [
      { h: "Record a purchase bill", p: "Purchases → Vendor Bills → New Bill. Select vendor, bill no, date, lines with GST rate." },
      { h: "ITC tracking", p: "GST paid on purchases posts to CGST/SGST/IGST Input ledgers — visible in Trial Balance under Assets. This is your Input Tax Credit available to offset output GST." },
      { h: "From PO", p: "If you created a PO first (ERP → Purchase Orders), click '→ Bill' on the PO row to auto-convert." },
    ],
  },
  {
    id: "expenses", title: "Expenses (Quick)", cat: "Purchases",
    body: [
      { h: "Simple expense entry", p: "Purchases → Expenses (Quick) → Record Expense. Best for one-off costs (rent, utilities). Auto-posts Dr Category-Expense, Cr Bank/Cash." },
      { h: "vs Travel & Expense", p: "Use T&E instead for employee-reimbursable expenses with receipts, mileage, per-diem, and approval workflow." },
    ],
  },
  {
    id: "tne", title: "Travel & Expense (T&E)", cat: "Purchases",
    body: [
      { h: "Trips", p: "Purchases → Travel & Expense → New Trip. Set purpose, destination, budget. All expenses linked to the trip roll up into a spent/approved view." },
      { h: "Expense claim", p: "New Expense → pick category (13 available: Airfare, Lodging, Meals, Transport, Fuel, Mileage, Per Diem, Office, Software, etc), amount, currency, receipt (≤500KB), payment method." },
      { h: "Mileage", p: "Choose Mileage category and enter km — amount auto-computes at ₹12/km." },
      { h: "Approval workflow", p: "Draft → Submit → Approve/Reject → Reimburse. Only Owner or Accountant can approve. On approval, Dr Category-Expense + Cr Employee Reimbursements Payable. On reimburse, Dr Payable + Cr Bank." },
    ],
  },
  {
    id: "gst", title: "GST Returns", cat: "Compliance",
    body: [
      { h: "GSTR-1 (Outward)", p: "Compliance → GST Returns → GSTR-1 tab. Aggregates all invoices for the selected month into B2B and B2C sections with taxable, CGST, SGST, IGST totals." },
      { h: "GSTR-3B (Summary)", p: "Monthly summary of outward tax + ITC claim. Net tax payable is calculated automatically." },
      { h: "GSTR-2B (ITC)", p: "Purchase-side reconciliation. Empty until you record purchase bills." },
      { h: "File to GSTN", p: "Click 'File to GSTN'. Generates a mock ARN and records in Filing History. Real GSTN push requires GSP integration (see e-Invoice GSP)." },
    ],
  },
  {
    id: "gsp", title: "e-Invoice GSP Config", cat: "Compliance",
    body: [
      { h: "Choose provider", p: "Compliance → e-Invoice GSP. Pick ClearTax, Masters India, or IRIS. Enter Client ID / Secret / Auth Token / User GSTIN. Toggle sandbox for testing." },
      { h: "Fallback", p: "Without configured GSP creds, the system generates mock IRNs (SHA-256 hashes) for demo. QR codes are real either way." },
    ],
  },
  {
    id: "bank", title: "Bank Reconciliation", cat: "Compliance",
    body: [
      { h: "Upload statement", p: "Compliance → Bank Recon → Upload Statement. CSV or XLSX. Standard columns: date, description, reference, credit, debit." },
      { h: "Auto-match", p: "For each unmatched transaction, click 'Match' to see suggested vouchers (by amount ± ₹1). Or click 'Voucher' to auto-create a matching voucher (Dr Bank if credit, Cr Bank if debit)." },
      { h: "Reconciliation summary", p: "Top KPIs show Statement Balance vs Book Balance and the difference. Goal: zero difference means fully reconciled." },
    ],
  },
  {
    id: "vouchers", title: "Vouchers (Journal)", cat: "Accounting",
    body: [
      { h: "Every transaction is a voucher", p: "Sales invoices, payments, expenses, purchase bills, credit notes, payroll runs — all post balanced double-entry vouchers automatically." },
      { h: "What you see", p: "Accounting → Vouchers lists every voucher with type, narration, date, entries (Dr/Cr with ledger and party), and total." },
    ],
  },
  {
    id: "coa", title: "Chart of Accounts", cat: "Accounting",
    body: [
      { h: "23 default ledgers", p: "Accounting → Chart of Accounts. Grouped as Assets, Liabilities, Income, Expense, Equity. Includes GST Input/Output, Sundry Debtors/Creditors, Bank, Cash, TDS Receivable/Payable." },
      { h: "Custom accounts", p: "Add your own ledgers via POST /api/chart-of-accounts. System-provided ledgers cannot be deleted." },
    ],
  },
  {
    id: "reports", title: "Reports", cat: "Accounting",
    body: [
      { h: "Trial Balance", p: "All ledgers with Debit, Credit and net Balance. Bottom row shows totals — should always balance (Dr = Cr)." },
      { h: "Profit & Loss", p: "Income (Sales + Other Income) minus Expenses = Net Profit." },
      { h: "Balance Sheet", p: "Assets (Debtors + Bank + Cash + Inventory) vs Liabilities (Creditors + GST Output payable) + Retained Earnings." },
    ],
  },
  {
    id: "erp-inventory", title: "Inventory & Stock", cat: "ERP",
    body: [
      { h: "Warehouses", p: "ERP → Inventory tab → Warehouse. Add one or more physical locations." },
      { h: "Stock items", p: "Link Product + Warehouse. Set opening stock, reorder level, cost price. Current stock updates automatically on manufacture / sales." },
      { h: "Stock movements", p: "Manual in/out adjustments post to stock_movements collection for audit trail." },
    ],
  },
  {
    id: "erp-so-po", title: "Sales & Purchase Orders", cat: "ERP",
    body: [
      { h: "Sales Order → Invoice", p: "Create SO with quantity/price/tax. Click '→ Invoice' to convert into a full GST invoice with double-entry." },
      { h: "Purchase Order → Bill", p: "Create PO, click '→ Bill' to convert into a Vendor Bill with ITC posting." },
    ],
  },
  {
    id: "erp-crm", title: "CRM Pipeline", cat: "ERP",
    body: [
      { h: "6 stages", p: "New → Qualified → Proposal → Negotiation → Won / Lost. Drag-free Kanban with stage dropdown per card." },
      { h: "Convert to customer", p: "When lead reaches 'Negotiation' or 'Won', click 'Convert →' to create a Customer record and link back to the lead." },
    ],
  },
  {
    id: "manufacturing", title: "Manufacturing / BOM", cat: "ERP",
    body: [
      { h: "Bill of Materials", p: "ERP → Manufacturing → New BOM. Define finished product + component list (product + qty per unit) + labour cost + overhead cost." },
      { h: "Produce", p: "Click Produce → pick BOM + warehouse + quantity. System validates raw stock, auto-consumes raw materials, adds finished goods to inventory." },
    ],
  },
  {
    id: "payroll", title: "Payroll & HR", cat: "ERP",
    body: [
      { h: "Add employees", p: "ERP → Payroll & HR → Employee. Enter Basic, HRA, Special Allowance, Other Allowance. Toggle PF/ESI/PT and set TDS %." },
      { h: "Statutory calc", p: "PF: 12% of Basic capped at ₹1,800/month (₹15k basic cap). ESI: 0.75% employee + 3.25% employer if gross ≤ ₹21k. PT: ₹200/month if gross > ₹10k." },
      { h: "Run payroll", p: "Pick month → Run Payroll. Generates a salary slip per employee and posts a consolidated voucher (Dr Salaries + Employer PF/ESI, Cr Bank + statutory payables + TDS)." },
    ],
  },
  {
    id: "agency", title: "Agency Portfolio (CA firms)", cat: "Partner",
    body: [
      { h: "Multi-tenant view", p: "Owners of multiple companies see the Agency Portfolio link in the sidebar. Shows aggregate KPIs across all client companies + per-client breakdown." },
      { h: "Switch context", p: "Click any client row (or use the sidebar company switcher) to switch active company. All modules re-scope to that client." },
      { h: "Bulk actions", p: "Preview or file GSTR-1/3B for selected clients in one call. Run recurring invoices across the entire portfolio." },
      { h: "Compliance calendar", p: "Amber strip lists pending GSTR filings + pending IRN generation across all clients, sorted by urgency." },
    ],
  },
  {
    id: "team", title: "Team & Roles", cat: "Admin",
    body: [
      { h: "Invite users", p: "Admin → Team & Roles → Invite User. Set initial password, role (Owner / Accountant / Viewer). If Resend is configured, they get an email; otherwise password is set directly." },
      { h: "Role permissions", p: "Owner: everything. Accountant: create/edit but no team invite or GSP config. Viewer: read-only, all writes return 403." },
    ],
  },
  {
    id: "approvals", title: "Approval Chains", cat: "Admin",
    body: [
      { h: "Amount-threshold rules", p: "Admin → Approval Chains → New Rule. Set min/max amount range, module (expense/purchase/invoice), required approver role, and level." },
      { h: "Example", p: "'Expense > ₹5000 needs Owner (L2)' means expenses ≥ ₹5k can only be approved by Owner, not Accountant." },
    ],
  },
  {
    id: "subscription", title: "Subscription Plans", cat: "Admin",
    body: [
      { h: "3 tiers", p: "Starter (₹499 / $9), Professional (₹1499 / $29), Enterprise (₹4999 / $99). Toggle INR/USD pricing at top." },
      { h: "Upgrade", p: "Click Upgrade on desired tier. In demo mode, upgrade is marked active immediately. Production wires in Razorpay checkout with live keys." },
    ],
  },
  {
    id: "faq", title: "FAQ & Troubleshooting", cat: "Help",
    body: [
      { h: "Cannot log in?", p: "Confirm you're using the right URL. If session expired, try Ctrl+Shift+R to bust cache. Demo accounts on the login page are one-click." },
      { h: "IRN not generating?", p: "Ensure invoice is B2B (customer has GSTIN), amount ≥ ₹50,000, and company's e-Invoice toggle is ON. Configure GSP creds under Compliance → e-Invoice GSP for real IRNs." },
      { h: "Trial Balance not balanced?", p: "Every transaction posts balanced entries automatically. If Dr ≠ Cr, contact support — it indicates a bug. Include a screenshot of the report." },
      { h: "Payroll not posting to books?", p: "Verify at least one active employee exists. Run Payroll posts the voucher only if there are employees to pay." },
      { h: "Emails not being sent?", p: "Team invite / invoice email require RESEND_API_KEY set in backend/.env and a verified sender email. Without it, payloads are logged to backend logs (no actual send)." },
    ],
  },
];

const CATEGORIES = ["Start here", "Setup", "Sales", "Purchases", "Compliance", "Accounting", "ERP", "Partner", "Admin", "Help"];

export default function Help() {
  const [q, setQ] = useState("");
  const [active, setActive] = useState("getting-started");

  const filtered = useMemo(() => {
    if (!q.trim()) return SECTIONS;
    const s = q.toLowerCase();
    return SECTIONS.filter(
      (sec) => sec.title.toLowerCase().includes(s) ||
               sec.body.some((b) => b.h.toLowerCase().includes(s) || b.p.toLowerCase().includes(s))
    );
  }, [q]);

  const activeSec = SECTIONS.find((s) => s.id === active) || SECTIONS[0];

  return (
    <div className="lb-fade" data-testid="help-page">
      <div className="mb-6">
        <div className="lb-section-head">Documentation</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">Help Center</h1>
        <p className="text-neutral-600 mt-1">Everything you need to run your books on LedgerBook Cloud.</p>
      </div>

      <div className="lb-card mb-5 p-3 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-neutral-500 ml-2" />
        <input placeholder="Search help — try 'GSTR', 'IRN', 'payroll', 'TDS', 'reconciliation'…"
               value={q} onChange={(e) => setQ(e.target.value)}
               className="bg-transparent outline-none text-sm flex-1 py-1"
               data-testid="help-search" />
      </div>

      <div className="grid grid-cols-12 gap-5">
        <aside className="col-span-3 lb-card p-3 sticky top-4 self-start max-h-[calc(100vh-120px)] overflow-y-auto">
          {CATEGORIES.map((cat) => {
            const items = filtered.filter((s) => s.cat === cat);
            if (!items.length) return null;
            return (
              <div key={cat} className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold px-2 py-1">
                  {cat}
                </div>
                {items.map((s) => (
                  <button key={s.id} onClick={() => setActive(s.id)}
                          className={"w-full text-left px-2 py-1.5 text-sm rounded-none transition-colors " +
                            (active === s.id ? "bg-[#0A66C2] text-white font-semibold" : "hover:bg-[#E7F3FF]")}
                          data-testid={`help-nav-${s.id}`}>
                    {s.title}
                  </button>
                ))}
              </div>
            );
          })}
        </aside>

        <main className="col-span-9 lb-card p-8" data-testid="help-content">
          <div className="lb-section-head mb-2">{activeSec.cat}</div>
          <h2 className="font-display font-extrabold text-3xl tracking-tight mb-6">{activeSec.title}</h2>

          <div className="space-y-6">
            {activeSec.body.map((item, i) => (
              <div key={i} className="border-l-2 border-neutral-200 pl-4 hover:border-[#0A66C2] transition-colors">
                <h3 className="font-display font-bold text-lg mb-1">{item.h}</h3>
                <p className="text-sm text-neutral-700 leading-relaxed">{item.p}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-neutral-200 grid md:grid-cols-2 gap-3">
            <div className="lb-panel-info p-4">
              <div className="flex items-center gap-2 mb-2">
                <Question size={16} /> <b>Still stuck?</b>
              </div>
              <p className="text-xs text-neutral-700">
                Every action in LedgerBook posts an audit log (Admin → Audit Logs).
                Share the log entry when contacting support so we can trace exactly what happened.
              </p>
            </div>
            <div className="lb-panel-accent p-4">
              <div className="flex items-center gap-2 mb-2">
                <Envelope size={16} /> <b>Contact support</b>
              </div>
              <p className="text-xs text-neutral-700">
                Email <b>support@ledgerbook.in</b> with your Company Name + GSTIN.
                Priority response for Professional & Enterprise plans.
              </p>
            </div>
          </div>

          <div className="mt-4 text-right">
            {SECTIONS.findIndex((s) => s.id === active) < SECTIONS.length - 1 && (
              <button onClick={() => setActive(SECTIONS[SECTIONS.findIndex((s) => s.id === active) + 1].id)}
                      className="lb-btn lb-btn-ghost lb-btn-sm">
                Next: {SECTIONS[SECTIONS.findIndex((s) => s.id === active) + 1].title}
                <ArrowRight size={12} />
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
